import { useMemo, useRef, useState } from "react";
import { bisectCenter, curveLinear, line } from "d3";

import { downsampleMinMax } from "../utils/downsample";
import { buildEcgGridPaths } from "./gridPath";

const UV_PER_MV = 1000;
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60000;

/** Default strip height: 30 mm of paper (±1.5 mV at 10 mm/mV). */
const DEFAULT_STRIP_HEIGHT_MM = 30;
const MIN_STRIP_WIDTH_PX = 40;

// ECG paper grid: Vivo blue at low opacity, heavier line every 5 mm.
const GRID_COLOR = "#64BDE1";
const GRID_MINOR_OPACITY = 0.25;
const GRID_MAJOR_OPACITY = 0.55;
const TRACE_COLOR = "#1f2a37";
const CROSSHAIR_COLOR = "#475569";
const CALIPER_COLOR = "#64BDE1";
const AXIS_WIDTH = 28;
const TICK_INTERVAL_MV = 0.5;

const formatMvLabel = (mv: number): string => {
  if (mv === 0) return "0";
  const fixed = mv.toFixed(1);
  return mv > 0 ? `+${fixed}` : fixed;
};

interface HoverState {
  x: number;
  sampleTimeMs: number;
  amplitudeMv: number;
}

interface CaliperState {
  startMs: number;
  endMs: number;
}

export interface ExtraTrace {
  timesMs: number[];
  amplitudesUv: number[];
  color: string;
}

export interface EcgLeadStripProps {
  leadName: string;
  timesMs: number[];
  amplitudesUv: number[];
  pxPerMm: number;
  mmPerSecond: number;
  mmPerMv: number;
  height?: number;
  showLabel?: boolean;
  /** Additional traces to overlay (compare mode). Each uses its own color. */
  extraTraces?: ExtraTrace[];
}

const formatHoverText = (hover: HoverState) =>
  `t=${(hover.sampleTimeMs / MS_PER_SECOND).toFixed(2)} s · ${hover.amplitudeMv.toFixed(2)} mV`;

const formatCaliperText = (caliper: CaliperState) => {
  const deltaMs = Math.abs(caliper.endMs - caliper.startMs);
  if (deltaMs < 1) return null;
  const bpm = MS_PER_MINUTE / deltaMs;
  return `Δt ${Math.round(deltaMs)} ms · ${bpm.toFixed(0)} bpm`;
};

/**
 * One ECG lead strip: paper grid + a single memoized trace path.
 * Hover crosshair and shift+drag caliper state live inside this component so
 * sibling strips never re-render on interaction.
 */
export const EcgLeadStrip = ({
  leadName,
  timesMs,
  amplitudesUv,
  pxPerMm,
  mmPerSecond,
  mmPerMv,
  height,
  showLabel = true,
  extraTraces,
}: EcgLeadStripProps) => {
  const [hover, setHover] = useState<HoverState | null>(null);
  const [caliper, setCaliper] = useState<CaliperState | null>(null);
  const draggingRef = useRef(false);

  const pxPerMs = (mmPerSecond * pxPerMm) / MS_PER_SECOND;
  const pxPerMv = mmPerMv * pxPerMm;
  const stripHeight = height ?? DEFAULT_STRIP_HEIGHT_MM * pxPerMm;
  const baselineY = stripHeight / 2;

  const t0 = timesMs.length ? timesMs[0] : 0;
  const tEnd = timesMs.length ? timesMs[timesMs.length - 1] : 0;
  const width = Math.max(
    Math.ceil((tEnd - t0) * pxPerMs) + 1,
    MIN_STRIP_WIDTH_PX,
  );

  const timeToX = (tMs: number) => (tMs - t0) * pxPerMs;
  const xToTime = (x: number) => t0 + x / pxPerMs;

  const plotted = useMemo(() => {
    const budget = Math.ceil(width * 2);
    return timesMs.length > budget
      ? downsampleMinMax(timesMs, amplitudesUv, budget)
      : { xs: timesMs, ys: amplitudesUv };
  }, [timesMs, amplitudesUv, width]);

  const tracePath = useMemo(() => {
    if (!plotted.xs.length) return "";
    const generator = line<[number, number]>()
      .x((d) => (d[0] - t0) * pxPerMs)
      .y((d) => baselineY - (d[1] / UV_PER_MV) * pxPerMv)
      .curve(curveLinear);
    const points = plotted.xs.map((x, i): [number, number] => [
      x,
      plotted.ys[i],
    ]);
    return generator(points) ?? "";
  }, [plotted, t0, pxPerMs, baselineY, pxPerMv]);

  const grid = useMemo(
    () => buildEcgGridPaths(width, stripHeight, pxPerMm),
    [width, stripHeight, pxPerMm],
  );

  /** Paths for overlay traces in compare mode (empty array when not comparing). */
  const extraTracePaths = useMemo(() => {
    if (!extraTraces?.length) return [];
    const budget = Math.ceil(width * 2);
    const generator = line<[number, number]>()
      .x((d) => (d[0] - t0) * pxPerMs)
      .y((d) => baselineY - (d[1] / UV_PER_MV) * pxPerMv)
      .curve(curveLinear);
    return extraTraces.map(
      ({ timesMs: eTimes, amplitudesUv: eAmps, color }) => {
        const ds =
          eTimes.length > budget
            ? downsampleMinMax(eTimes, eAmps, budget)
            : { xs: eTimes, ys: eAmps };
        const points = ds.xs.map((x, i): [number, number] => [
          x - eTimes[0] + t0,
          ds.ys[i],
        ]);
        return { path: generator(points) ?? "", color };
      },
    );
  }, [extraTraces, width, t0, pxPerMs, baselineY, pxPerMv]);

  const localX = (e: React.MouseEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientX - rect.left;
  };

  const handleMouseMove = (e: React.MouseEvent<SVGRectElement>) => {
    const x = localX(e);
    const tMs = xToTime(x);
    if (plotted.xs.length) {
      const idx = Math.max(
        0,
        Math.min(plotted.xs.length - 1, bisectCenter(plotted.xs, tMs)),
      );
      setHover({
        x,
        sampleTimeMs: plotted.xs[idx],
        amplitudeMv: plotted.ys[idx] / UV_PER_MV,
      });
    }
    if (draggingRef.current) {
      setCaliper((prev) => (prev ? { ...prev, endMs: tMs } : prev));
    }
  };

  const handleMouseDown = (e: React.MouseEvent<SVGRectElement>) => {
    const tMs = xToTime(localX(e));
    if (e.shiftKey) {
      e.preventDefault();
      draggingRef.current = true;
      setCaliper({ startMs: tMs, endMs: tMs });
    } else {
      setCaliper(null);
    }
  };

  const handleMouseUp = () => {
    draggingRef.current = false;
  };

  const handleMouseLeave = () => {
    draggingRef.current = false;
    setHover(null);
  };

  const caliperLeft = caliper
    ? timeToX(Math.min(caliper.startMs, caliper.endMs))
    : 0;
  const caliperRight = caliper
    ? timeToX(Math.max(caliper.startMs, caliper.endMs))
    : 0;
  const caliperText = caliper ? formatCaliperText(caliper) : null;

  // Y-axis ticks in mV for the visible amplitude range.
  const axisOffset = showLabel ? AXIS_WIDTH : 0;
  const mvTicks = useMemo(() => {
    if (!showLabel || pxPerMv <= 0) return [];
    const rangeTopMv = baselineY / pxPerMv;
    const rangeBottomMv = -baselineY / pxPerMv;
    const startTick =
      Math.ceil(rangeBottomMv / TICK_INTERVAL_MV) * TICK_INTERVAL_MV;
    const endTick =
      Math.floor(rangeTopMv / TICK_INTERVAL_MV) * TICK_INTERVAL_MV;
    const ticks: number[] = [];
    for (
      let mv = startTick;
      mv <= endTick + TICK_INTERVAL_MV * 0.001;
      mv += TICK_INTERVAL_MV
    ) {
      ticks.push(Math.round(mv * 10) / 10);
    }
    return ticks;
  }, [showLabel, pxPerMv, baselineY]);

  return (
    <div className="relative" style={{ width, height: stripHeight }}>
      <svg width={width} height={stripHeight} className="block select-none">
        {/* Y-axis with mV ticks — only when labels are shown */}
        {showLabel && (
          <g pointerEvents="none">
            {mvTicks.map((mv) => {
              const y = baselineY - mv * pxPerMv;
              const isBaseline = mv === 0;
              return (
                <g key={mv}>
                  <line
                    x1={AXIS_WIDTH - 4}
                    x2={AXIS_WIDTH}
                    y1={y}
                    y2={y}
                    stroke={TRACE_COLOR}
                    strokeWidth={isBaseline ? 1.5 : 0.75}
                    strokeOpacity={isBaseline ? 0.7 : 0.45}
                  />
                  <text
                    x={AXIS_WIDTH - 6}
                    y={y + 3}
                    fontSize={9}
                    textAnchor="end"
                    fill={TRACE_COLOR}
                    fillOpacity={isBaseline ? 0.75 : 0.5}
                    fontWeight={isBaseline ? 600 : 400}
                  >
                    {formatMvLabel(mv)}
                  </text>
                </g>
              );
            })}
          </g>
        )}
        {/* Grid and trace shifted right by axisOffset */}
        <g transform={`translate(${axisOffset},0)`}>
          <path
            d={grid.minor}
            fill="none"
            stroke={GRID_COLOR}
            strokeOpacity={GRID_MINOR_OPACITY}
            strokeWidth={0.5}
          />
          <path
            d={grid.major}
            fill="none"
            stroke={GRID_COLOR}
            strokeOpacity={GRID_MAJOR_OPACITY}
            strokeWidth={1}
          />
          <path
            d={tracePath}
            fill="none"
            stroke={TRACE_COLOR}
            strokeWidth={1.25}
          />
          {extraTracePaths.map(({ path, color }, idx) => (
            <path
              key={idx}
              d={path}
              fill="none"
              stroke={color}
              strokeWidth={1.25}
              strokeOpacity={0.85}
            />
          ))}
          {caliper && (
            <g pointerEvents="none">
              <rect
                x={caliperLeft}
                y={0}
                width={Math.max(caliperRight - caliperLeft, 0)}
                height={stripHeight}
                fill={CALIPER_COLOR}
                fillOpacity={0.18}
              />
              <line
                x1={caliperLeft}
                x2={caliperLeft}
                y1={0}
                y2={stripHeight}
                stroke={CALIPER_COLOR}
                strokeWidth={1}
              />
              <line
                x1={caliperRight}
                x2={caliperRight}
                y1={0}
                y2={stripHeight}
                stroke={CALIPER_COLOR}
                strokeWidth={1}
              />
            </g>
          )}
          {hover && (
            <line
              pointerEvents="none"
              x1={hover.x}
              x2={hover.x}
              y1={0}
              y2={stripHeight}
              stroke={CROSSHAIR_COLOR}
              strokeOpacity={0.6}
              strokeDasharray="3,3"
            />
          )}
          <rect
            x={0}
            y={0}
            width={width - axisOffset}
            height={stripHeight}
            fill="transparent"
            className="cursor-crosshair"
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          />
          {showLabel && (
            <text
              x={4}
              y={14}
              fontSize={12}
              fontWeight={600}
              fill={TRACE_COLOR}
              pointerEvents="none"
            >
              {leadName}
            </text>
          )}
        </g>
      </svg>
      {hover && (
        <div
          className="absolute pointer-events-none z-10 bg-gray-800 text-white text-[11px] rounded px-2 py-0.5 whitespace-nowrap"
          style={{
            left: hover.x + axisOffset + 10,
            top: 6,
            transform:
              hover.x + axisOffset > width * 0.8
                ? "translateX(-120%)"
                : undefined,
          }}
        >
          {formatHoverText(hover)}
        </div>
      )}
      {caliperText && (
        <div
          className="absolute pointer-events-none z-10 bg-white/90 border border-gray-300 text-gray-700 text-[11px] rounded px-2 py-0.5 whitespace-nowrap -translate-x-1/2"
          style={{
            left: Math.max((caliperLeft + caliperRight) / 2, 40),
            top: 2,
          }}
        >
          {caliperText}
        </div>
      )}
    </div>
  );
};
